import { useDependencies } from '@/scopes/common/infrastructure/dependencies';
import { useFiltersQuery } from '@/domain/prices/filters/hooks';
import { usePricesStore } from '@/infrastructure/prices/usePricesStore';
import { useAccommodationContext } from '@/react-pages/Prices/AccommodationContext';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { partial } from 'ramda';
import * as React from 'react';
import type { RoomInfo, Stock } from './types';
import { useAccommodationAvailability } from '@/domain/prices/availability/hooks';

export function useRoomsQuery() {
  const { roomSelectorApiClient } = useDependencies();
  const { contextItemId, accoId, bookingGateId } = useAccommodationContext();
  const { filterSelection } = useFiltersQuery();
  const { selectedPriceId } = usePricesStore();
  const { isFlightOnly } = useAccommodationAvailability();

  const filtersCriteria = {
    contextItemId,
    bookingGateId,
    accoId,
    filterSelection,
    selectedPriceId: selectedPriceId || '',
  };

  return useQuery({
    queryKey: ['rooms', filtersCriteria],
    queryFn: () => roomSelectorApiClient.getRooms(filtersCriteria),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
    enabled: Boolean(selectedPriceId) && !isFlightOnly,
  });
}

export function useRoomSelector() {
  const { data, isFetching, isError } = useRoomsQuery();
  const { selectedRooms } = usePricesStore();

  const allRoomsSelected =
    selectedRooms && Object.values(selectedRooms).filter((value) => value !== null).length === data?.roomGroups?.length;

  const selectedRoomIds: string[] = [];
  const selectedRoomContractIds: string[] = [];
  const selectedRoomInfos: RoomInfo[] = [];

  if (allRoomsSelected) {
    for (const [roomNumber, roomId] of Object.entries(selectedRooms)) {
      if (roomId !== null) {
        const roomToAdd = data?.roomGroups
          ?.find((roomGroup) => roomGroup.roomNumber === roomNumber)
          ?.rooms?.find((room) => room.id === roomId);
        if (roomToAdd) {
          selectedRoomIds.push(roomToAdd.id);
          selectedRoomContractIds.push(roomToAdd.contractId);
          selectedRoomInfos.push(roomToAdd);
        }
      }
    }
  }

  return {
    roomGroups: data?.roomGroups || [],
    isFetching,
    isError,
    stock: data?.stock || {},
    specialAgeProfiles: data?.specialAgeProfiles || {},
    allRoomsSelected,
    selectedRoomIds,
    selectedRoomContractIds,
    selectedRooms: selectedRoomInfos,
  };
}

export function useRoomGroup(roomNumber: string) {
  const { roomGroups, stock } = useRoomSelector();
  const { selectedRooms, setSelectedRoom } = usePricesStore();

  const updatedStock = React.useMemo((): Stock => calculateUpdatedStock(stock, selectedRooms), [stock, selectedRooms]);

  const roomGroup = roomGroups.find((group) => group.roomNumber === roomNumber);

  return {
    rooms: roomGroup?.rooms || [],
    selectedRoom: selectedRooms?.[roomNumber],
    setSelectedRoom: partial(setSelectedRoom, [roomNumber]),
    stock: updatedStock,
    distribution: roomGroup?.distribution,
  };
}

export function useRoom(roomNumber: string, roomId: string) {
  const { rooms, stock, selectedRoom } = useRoomGroup(roomNumber);
  const { stock: apiStock } = useRoomSelector();
  const { setSelectedRoom } = usePricesStore();

  const isSelected = selectedRoom === roomId;

  return {
    room: rooms.find((room) => room.id === roomId),
    stock: stock[roomId],
    apiStock: apiStock[roomId],
    setSelectedRoom: partial(setSelectedRoom, [roomNumber]),
    isSelected,
  };
}

function calculateUpdatedStock(currentStock: Stock, currentSelectedRooms: Record<string, string | null> | null): Stock {
  const updatedStock: Stock = { ...currentStock };

  for (const r in currentSelectedRooms) {
    const roomId = currentSelectedRooms[r];
    if (roomId && updatedStock[roomId] !== undefined) {
      updatedStock[roomId] -= 1;
    }
  }
  return updatedStock;
}
